import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FormHeader from "../../../../components/dms_common/FormHeader";
import DropdownSelector from "../../components/dropdownSelector/DropdownSelector";
import CreditNote from "../../components/creditNote/CreditNote";
import PaymentDetails from "../../components/paymentDetails/PaymentDetails";
import PaymentSummary from "../../components/collectionFooter/PaymentSummary";
import CreditCustomer from "../../components/creditCustomer/CreditCustomer";
import {
  handleSubmit,
  preparePayload,
  fetchCreditNotes,
  getUnpaidInvoicesByOutlet,
} from "../../services/collectionService";
import {
  BaseOption,
  collectionStatusMapping,
  CreditNoteData,
  InvoiceOption,
  paymentModes,
  PaymentDetail,
  PaymentStatusStyling,
  InvoiceData,
  ModalConfig,
  isAmountGreater,
} from "../../collectionUtils";
import "./RecordPayment.css";
import CustomGenericModal from "../../../../components/dms_common/CustomGenericModal";
import { truncateToTwoDecimals } from "../../../../components/dms_common/DmsUtils";
import { getConfigData } from "../../../../services/configurationService";
import { set } from "lodash";

const RecordPayment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search || "");
  const type = queryParams.get("type") ?? "";
  const { outletDetails = {}, startDate, endDate } = location.state || {};
  const outletCode = outletDetails?.outletCode || "";

  const isCreditPage = type === "credit";
  const isCashPage = type === "cash";

  const [selectInvoiceOpen, setSelectInvoiceOpen] = useState<boolean>(false);
  const [modeOfPaymentOpen, setModeOfPaymentOpen] = useState<boolean>(false);
  const [creditNoteOpen, setCreditNoteOpen] = useState<boolean>(true);
  const [creditCustomerOpen, setCreditCustomerOpen] = useState<boolean>(true);
  const [selectedInvoice, setSelectedInvoice] = useState<ExtendedInvoiceOption | null>(null);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([]);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetail[]>([]);
  const [paymentDetailsOpen, setPaymentDetailsOpen] = useState<boolean[]>([]);
  const [creditNotes, setCreditNotes] = useState<CreditNoteData[]>([]);
  const [loadingCreditNotes, setLoadingCreditNotes] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [useExpiredCreditNotes, setUseExpiredCreditNotes] = useState<boolean>(false);

  // Add ref for the payment mode dropdown
  const paymentModeRef = useRef<HTMLDivElement>(null);

  // Click outside handler for payment mode dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (paymentModeRef.current && !paymentModeRef.current.contains(event.target as Node)) {
        setModeOfPaymentOpen(false);
      }
    };

    if (modeOfPaymentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modeOfPaymentOpen]);

  // Fetch credit notes
  useEffect(() => {
    const fetchData = async () => {
      if (!outletCode) {
        console.error("Outlet code is missing");
        return;
      }
      setLoadingCreditNotes(true);
      try {
        const fetchedCreditNotes = await fetchCreditNotes(outletCode);
        const updatedUseExpiredCreditNotes = await getConfigData("dms_configuration", "useExpiredCreditNotes");
        setUseExpiredCreditNotes(updatedUseExpiredCreditNotes?.[0]?.configValue ?? false);
        setCreditNotes(fetchedCreditNotes);
      } catch (error) {
        console.error("Error fetching credit notes:", error);
        setCreditNotes([]);
      } finally {
        setLoadingCreditNotes(false);
      }
    };
    fetchData();
  }, [outletCode]);

  // Fetch invoices for cash page
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!isCashPage || !outletCode) return;
      setLoadingInvoices(true);
      try {
        const fetchedInvoices = await getUnpaidInvoicesByOutlet(outletCode);
        setInvoices(fetchedInvoices);
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };
    fetchInvoices();
  }, [isCashPage, outletCode]);

  interface ExtendedInvoiceOption extends InvoiceOption {
    pendingAmount: number;
  }

  const invoiceOptions: ExtendedInvoiceOption[] = useMemo(() => {
    if (!isCashPage || !invoices || !Array.isArray(invoices)) return [];
    return invoices
      .filter((invoice): invoice is InvoiceData =>
        invoice && typeof invoice.invoiceNumber === "string" && invoice.invoiceNumber.trim() !== ""
      )
      .map((invoice) => {
        const collectionStatuses = collectionStatusMapping?.() || [];
        const paymentStatusStyling: PaymentStatusStyling =
          collectionStatuses.find(
            (status: any) => status?.id?.toLowerCase() === invoice.status?.toLowerCase()
          ) ?? {
            dmsLabelColor: "#000000",
            dmsLabel: invoice.status || "Pending",
            dmsColor: "#FFFFFF",
          };
        return {
          id: invoice.invoiceNumber,
          label: `${invoice.invoiceNumber} - ₹${truncateToTwoDecimals(invoice.pendingAmount)}`,
          paymentStatus: invoice.status || "Pending",
          statusStyling: paymentStatusStyling,
          pendingAmount: invoice.pendingAmount,
        };
      });
  }, [isCashPage, invoices]);

  const paymentModeOptions: BaseOption[] = useMemo(() => {
    if (!paymentModes || !Array.isArray(paymentModes)) return [];
    return paymentModes
      .map((mode) => ({
        id: mode || "",
        label: mode || "",
      }))
      .filter((option) => option.id && option.label);
  }, []);

  // Calculate total amount and select credit notes
  const totalAmount = useMemo(() => {
    if (isCashPage) {
      return selectedInvoice?.pendingAmount || 0;
    } else if (isCreditPage) {
      return outletDetails?.creditAmount || 0;
    }
    return 0;
  }, [isCashPage, isCreditPage, selectedInvoice, outletDetails]);

  const paymentDetailsTotal = useMemo(() => {
    return paymentDetails.reduce((sum, detail) => sum + (detail?.amount || 0), 0);
  }, [paymentDetails]);

  const { selectedCreditNotes, creditNotesTotal } = useMemo(() => {
    // Filter credit notes based on useExpiredCreditNotes
    const availableCreditNotes = creditNotes.filter(
      (note) => useExpiredCreditNotes || !note.expired
    );
    // Sort by endDate (nearest first)
    availableCreditNotes.sort((a, b) => {
  const dateA = new Date(a.endDate);
  const dateB = new Date(b.endDate);
  // Handle invalid dates by falling back to a default (e.g., far past date)
  if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
    console.warn("Invalid endDate encountered:", { a, b });
    return 0; // Treat invalid dates as equal to avoid sorting issues
  }
  return dateA.getTime() - dateB.getTime();
});
    
    let remaining = totalAmount - paymentDetailsTotal;
    if (remaining <= 0) {
      return { selectedCreditNotes: [], creditNotesTotal: 0 };
    }

    let sum = 0;
    const selected = [];
    for (const note of availableCreditNotes) {
      if (sum >= remaining) break;
      const useAmount = Math.min(note.availableAmount, remaining - sum);
      if (useAmount > 0) {
        selected.push({ ...note, usedAmount: useAmount });
        sum += useAmount;
      }
    }
    return { selectedCreditNotes: selected, creditNotesTotal: sum };
  }, [creditNotes, useExpiredCreditNotes, paymentDetailsTotal, totalAmount]);

  const collectedAmount = paymentDetailsTotal + creditNotesTotal;
  const balanceAmount = totalAmount - collectedAmount;

  const paymentSummaryAmounts = useMemo(() => {
    return {
      totalAmount,
      collectedAmount,
      balanceAmount,
    };
  }, [totalAmount, collectedAmount, balanceAmount]);

  // Validation for payment details
  const validatePaymentDetails = useMemo(() => {
    if (!paymentDetails || paymentDetails.length === 0) return true;
    return paymentDetails.every((detail) => {
      if (!detail.amount || detail.amount <= 0) return false;
      switch (detail.mode) {
        case "UPI":
          return detail.transactionId && detail.transactionId.trim() !== "";
        case "Cheque":
          return (
            detail.chequeDate &&
            detail.chequeDate.trim() !== "" &&
            detail.bankName &&
            detail.bankName.trim() !== "" &&
            detail.bankAccountNo &&
            detail.bankAccountNo.trim() !== "" &&
            detail.ifscCode &&
            detail.ifscCode.trim() !== ""
          );
        case "NEFT":
          return (
            detail.payeeName &&
            detail.payeeName.trim() !== "" &&
            detail.bankName &&
            detail.bankName.trim() !== "" &&
            detail.bankAccountNo &&
            detail.bankAccountNo.trim() !== "" &&
            detail.ifscCode &&
            detail.ifscCode.trim() !== "" &&
            detail.transactionId &&
            detail.transactionId.trim() !== ""
          );
        case "Cash":
          return true;
        default:
          return true;
      }
    });
  }, [paymentDetails]);

  // Form validation
  const isFormValid = useMemo(() => {
    if (isCashPage && !selectedInvoice) return false;
    return validatePaymentDetails && paymentSummaryAmounts.collectedAmount > 0;
  }, [isCashPage, selectedInvoice, validatePaymentDetails, paymentSummaryAmounts.collectedAmount]);

  // Sync payment details with selected methods
  useEffect(() => {
    if (!selectedPaymentMethods || selectedPaymentMethods.length === 0) {
      setPaymentDetails([]);
      setPaymentDetailsOpen([]);
      return;
    }
    const newPaymentDetails = selectedPaymentMethods.map((mode) => {
      const existingDetail = paymentDetails.find((detail) => detail.mode === mode);
      return existingDetail || {
        mode: mode || "",
        amount: 0,
        transactionId: mode === "UPI" || mode === "NEFT" ? "" : undefined,
        chequeDate: mode === "Cheque" ? "" : undefined,
        bankAccountNo: mode === "Cheque" || mode === "NEFT" ? "" : undefined,
        bankName: mode === "Cheque" || mode === "NEFT" ? "" : undefined,
        ifscCode: mode === "Cheque" || mode === "NEFT" ? "" : undefined,
        payeeName: mode === "NEFT" ? "" : undefined,
      };
    });
    const newPaymentDetailsOpen = selectedPaymentMethods.map((mode, index) => {
      const existingIndex = paymentDetails.findIndex((detail) => detail.mode === mode);
      return existingIndex !== -1 && paymentDetailsOpen[existingIndex] !== undefined
        ? paymentDetailsOpen[existingIndex]
        : true;
    });
    setPaymentDetails(newPaymentDetails);
    setPaymentDetailsOpen(newPaymentDetailsOpen);
  }, [selectedPaymentMethods]);

  const handleInvoiceSelect = (option: BaseOption | InvoiceOption): void => {
    if (!option) return;
    setSelectedInvoice(option as ExtendedInvoiceOption);
    setSelectInvoiceOpen(false);
  };

  const handleRemoveInvoice = (event: React.MouseEvent): void => {
    if (event) event.stopPropagation();
    setSelectedInvoice(null);
  };

  const handlePaymentMethodToggle = (optionId: string): void => {
    if (!optionId) return;
    setSelectedPaymentMethods((prev) => {
      const prevMethods = prev || [];
      return prevMethods.includes(optionId)
        ? prevMethods.filter((method) => method !== optionId)
        : [...prevMethods, optionId];
    });
  };

  const handleRemovePaymentMethod = (optionId: string, event: React.MouseEvent): void => {
    if (!optionId || !event) return;
    event.stopPropagation();
    setSelectedPaymentMethods((prev) => (prev || []).filter((method) => method !== optionId));
  };

  const handleUpdatePaymentDetail = (updatedDetail: PaymentDetail, index: number) => {
    if (!updatedDetail || typeof index !== "number" || index < 0) return;
    setPaymentDetails((prev) => {
      const prevDetails = prev || [];
      if (index >= prevDetails.length) return prevDetails;
      const newDetails = [...prevDetails];
      newDetails[index] = updatedDetail;
      return newDetails;
    });
  };

  const onSubmit = async () => {
    try {
      if (
        !isFormValid ||
        isAmountGreater(paymentSummaryAmounts.collectedAmount, paymentSummaryAmounts.totalAmount)
      ) {
        throw new Error(
          "Please ensure all fields are correctly filled and total paid amount does not exceed the total amount."
        );
      }

      setIsSubmitting(true);

      const payload = preparePayload({
        paymentDetails: paymentDetails || [],
        creditNotes: selectedCreditNotes || [], // Pass selected credit notes
        outletCode: outletCode || "",
        type: type || "",
        selectedInvoice,
        useExpiredCreditNotes,
        totalAmount: paymentSummaryAmounts.totalAmount,
      });

      await handleSubmit(payload);

      setModalConfig({
        type: "success",
        message: `Payment has been collected for ${outletDetails?.outletName}`,
        leftButtonText: "Continue",
        onLeftButtonClick: () => {
          setModalOpen(false);
          navigate("/dashboard/collection/outletDetails", {
            state: {
              outletCode: outletDetails?.outletCode,
              startDate,
              endDate,
            },
          });
        },
        onClose: () => {
          setModalOpen(false);
          navigate("/dashboard/collection/outletDetails", {
            state: {
              outletCode: outletDetails?.outletCode,
              startDate,
              endDate,
            },
          });
        },
      });
      setModalOpen(true);
    } catch (error) {
      setModalConfig({
        type: "error",
        message: `Error while collecting payment`,
        leftButtonText: "Continue",
        onLeftButtonClick: () => setModalOpen(false),
      });
      setModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const safeUtilizedCredit = outletDetails?.creditAmount ?? 0;
  const safeCreditLimit = outletDetails?.creditLimit ?? 0;
  const safeAvailableLimit = outletDetails?.creditAvailable ?? 0;
  const safeCurrentOutstanding = outletDetails?.creditAmount ?? 0;

    return (
        <div className="CreditAndCashCollection_container">
            <FormHeader
                parentLabel={"Collection"}
                label={"Outlet Details"}
                goBackToParent={() =>
                    navigate("/dashboard/collection/outletDetails", {
                        state: {
                            outletCode: outletDetails?.outletCode,
                            startDate,
                            endDate,
                        },
                    })
                }
                childLabel={"Cash Collection"}
                childLableCaseClick={() => navigate("/dashboard/collection")}
            />

            <div className="CreditAndCashCollection_content_container">
                <div className="CreditAndCashCollection_content">
                    {isCashPage && (
                        <div style={{ marginBottom: "30px" }}>
                            <DropdownSelector
                                label="Invoice"
                                placeholder={loadingInvoices ? "Loading invoices..." : "Select Invoice"}
                                options={invoiceOptions}
                                isOpen={selectInvoiceOpen}
                                onToggle={() => setSelectInvoiceOpen(!selectInvoiceOpen)}
                                selectedValue={selectedInvoice}
                                onSingleSelect={handleInvoiceSelect}
                                onRemoveSingle={handleRemoveInvoice}
                                multiSelect={false}
                                showCustomRadio={true}
                                required={true}
                                singleSelectTagStyle={{ backgroundColor: "#EFF2FF", color: "#1E40CC" }}
                            />
                        </div>
                    )}

                    {isCreditPage && (
                        <div style={{ marginBottom: "30px" }}>
                            <CreditCustomer
                                isOpen={creditCustomerOpen}
                                onToggle={() => setCreditCustomerOpen(!creditCustomerOpen)}
                                utilizedCredit={safeUtilizedCredit}
                                creditLimit={safeCreditLimit}
                                availableLimit={safeAvailableLimit}
                                currentOutstanding={safeCurrentOutstanding}
                            />
                        </div>
                    )}

                    {creditNotes && creditNotes.length > 0 && (
                        <div style={{ margin: "35px 0px" }}>
                            <CreditNote
                                creditNotes={creditNotes}
                                isOpen={creditNoteOpen}
                                onToggle={() => setCreditNoteOpen(!creditNoteOpen)}
                            />
                        </div>
                    )}

                    <div ref={paymentModeRef}>
                        <DropdownSelector
                            label="Mode of Payment"
                            placeholder="Select Payment Modes"
                            options={paymentModeOptions}
                            isOpen={modeOfPaymentOpen}
                            onToggle={() => setModeOfPaymentOpen(!modeOfPaymentOpen)}
                            selectedValues={selectedPaymentMethods || []}
                            onMultiSelect={handlePaymentMethodToggle}
                            onRemoveMulti={handleRemovePaymentMethod}
                            multiSelect={true}
                            showCustomRadio={false}
                            required={false} // Optional if credit notes cover the amount
                            multiSelectTagStyle={{ backgroundColor: "#F0FBFF", color: "#16B4A3" }}
                        />
                    </div>

           {(selectedPaymentMethods || []).length > 0 && (
  <div className="CreditAndCashCollection_payment_details_container">
    {(paymentDetails || []).map((detail, index) => (
      detail && (
        <PaymentDetails
          key={`${detail.mode || "unknown"}-${index}`}
          paymentDetail={detail}
          parentIndex={index}
          isOpen={paymentDetailsOpen[index] ?? true}
          onToggle={() => {
            setPaymentDetailsOpen((prev) => {
              const prevOpen = prev || [];
              const newOpen = [...prevOpen];
              while (newOpen.length <= index) newOpen.push(true);
              newOpen[index] = !newOpen[index];
              return newOpen;
            });
          }}
          onUpdate={handleUpdatePaymentDetail}
          totalAmount={paymentSummaryAmounts.totalAmount}
          collectedAmount={paymentSummaryAmounts.collectedAmount}
          onOpenModal={(message: string) => {
            setModalConfig({
              type: "error",
              message,
              leftButtonText: "Continue",
              onLeftButtonClick: () => setModalOpen(false),
            });
            setModalOpen(true);
          }}
        />
      )
    ))}
  </div>
)}

                  
                </div>
                  <PaymentSummary
                        totalAmount={paymentSummaryAmounts.totalAmount}
                        paidAmount={paymentSummaryAmounts.collectedAmount}
                        balanceAmount={paymentSummaryAmounts.balanceAmount}
                        onSubmit={onSubmit}
                        submitButtonText="Submit"
                        isSubmitDisabled={
                            !isFormValid ||
                            loadingInvoices ||
                            isAmountGreater(paymentSummaryAmounts.collectedAmount, paymentSummaryAmounts.totalAmount)
                        }
                        isLoading={isSubmitting}
                    />
            </div>

            {modalConfig && (
                <CustomGenericModal
                    {...modalConfig}
                    modalOpen={modalOpen}
                />
            )}
        </div>
    );
};

export default RecordPayment;